import { registerAuditLabels } from "@vixio/ui";
import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap, Tone } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo inventory (ver modules/inventory/README.md). */

export type LevelKey = "critico" | "fragmentado" | "organizado" | "integrado";

export const LEVELS: Array<{ key: LevelKey; label: string; min: number; max: number; tone: Tone }> = [
  { key: "critico", label: "Crítico", min: 0, max: 39, tone: "crit" },
  { key: "fragmentado", label: "Fragmentado", min: 40, max: 59, tone: "warn" },
  { key: "organizado", label: "Organizado", min: 60, max: 79, tone: "info" },
  { key: "integrado", label: "Integrado", min: 80, max: 100, tone: "ok" },
];
export const LEVEL_STATUS: StatusMap = Object.fromEntries(LEVELS.map((l) => [l.key, { label: l.label, tone: l.tone }]));

/** As 10 perguntas do PM3, com rótulo curto para tabela e gráfico. */
export const QUESTIONS: Array<{ key: string; short: string }> = [
  { key: "sistemas", short: "Quantos sistemas" },
  { key: "usuarios", short: "Quem usa" },
  { key: "entrada", short: "Entrada do dado" },
  { key: "destino", short: "Destino do dado" },
  { key: "senhas", short: "Senhas por pessoa" },
  { key: "planilhas", short: "Planilhas paralelas" },
  { key: "integracoes", short: "Integrações" },
  { key: "treinamento", short: "Treinamento" },
  { key: "dono", short: "Dono do dado" },
  { key: "saida", short: "Contrato de saída" },
];
export const questionShort = (k: string) => QUESTIONS.find((q) => q.key === k)?.short ?? k;

export interface InventoryResult {
  id: string;
  org_id: string;
  response_id: string;
  person_id: string | null;
  person_name: string | null;
  person_email: string | null;
  person_phone: string | null;
  company_name: string | null;
  instrument_version: string;
  score: number;
  level: LevelKey;
  points: Array<{ key: string; answer: string; points: number }>;
  risks: string[];
  recommendations: Array<{ kind: "level" | "question" | "closing"; key: string; text: string }>;
  context: Record<string, string | undefined>;
  created_at: string;
}

export interface InventoryInstrument {
  version: string;
  definition: {
    title: string;
    intro: string;
    disclaimer: string;
    evidence: string;
    items: Array<{ id: string; text: string; options: Array<{ value: string; label: string; points: number }> }>;
  };
}

export interface RecommendationText {
  kind: "level" | "question" | "closing";
  key: string;
  text: string;
  default_text: string;
  customized: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export function useInventoryInstrument() {
  return useQuery({
    queryKey: ["inventory", "instrument"],
    staleTime: 10 * 60_000,
    queryFn: async () =>
      (await unwrap<InventoryInstrument[]>(from("inventory", "instrument_versions").select("version, definition").order("created_at", { ascending: false }).limit(1)))[0] ??
      null,
  });
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "inventory.install": { args: { p_org: string }; returns: string };
    "inventory.set_recommendation": { args: { p_org: string; p_kind: RecommendationText["kind"]; p_key: string; p_text: string }; returns: string };
    "inventory.recommendation_texts": { args: { p_org: string }; returns: RecommendationText[] };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "inventory.installed": "Inventário instalado",
  "inventory.recommendation_set": "Texto da recomendação alterado",
});
